import * as React from 'react';
import {Dimensions, Image, ScrollView, StyleSheet, View} from 'react-native';
import ScreenContext from '../../ScreenContext';
import BackSubheader from '../../elements/BackSubheader';
import Styles from '../../Styles';
import DealRepository, {DealStatus} from 'src/services/aws/DealRepository';
import Events, {PromotionActions} from 'src/logging/Events';
import AVText from '../../elements/AVText';
import Localized from 'src/constants/AppStrings';
import Deal from 'src/models/Moblico/Deal';
import {ensureHttps} from 'src/services/StringUtils';
import Util, {getTransactionDate} from 'src/Util';
import DealHelper from 'src/components/helpers/DealHelper';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import {NotificationMessageParams} from 'src/redux/thunks/notificationThunks/messageRead';
import AccountStore from 'src/stores/AccountStore';
import {getDescriber} from './descriptor/DescriptorType';
import {RouteProp} from '@react-navigation/native';
import {StackNavigationProp} from '@react-navigation/stack';
import {MessageTypeEnum} from 'src/constants/MessageTypeEnum';
import {WithGlobalizeProps} from '../../../i18n/HOC/withGlobalize';

type AppRoutesParamList = {
  DealDetail: {
    deal: Deal;
  };
};

type DealDetailScreenProps = WithGlobalizeProps & {
  navigation: StackNavigationProp<AppRoutesParamList, 'DealDetail'>;
  route: RouteProp<AppRoutesParamList, 'DealDetail'>;
  loading: boolean;
  markMessageAsRead(params: NotificationMessageParams): void;
};
type DealDetailScreenState = {
  deal: Deal;
  loading: boolean;
};

const width = Dimensions.get('window').width;

class DealDetailScreen extends React.Component<
  DealDetailScreenProps,
  DealDetailScreenState
> {
  static contextType = ScreenContext;
  declare context: React.ContextType<typeof ScreenContext>;

  constructor(props: DealDetailScreenProps) {
    super(props);
    const dealFromParams = props.route?.params?.deal;
    if (!dealFromParams) {

    }

    const dealInstance =
      dealFromParams instanceof Deal
        ? dealFromParams
        : new Deal(dealFromParams ?? {});

    this.state = {
      deal:
        dealInstance?.externalServiceId != null
          ? DealRepository.getDeal(String(dealInstance.externalServiceId)) ??
            dealInstance
          : dealInstance,
      loading: false,
    };

    this.applyDeal = this.applyDeal.bind(this);
    this.renderApplyButton = this.renderApplyButton.bind(this);
    this.onDealsChanged = this.onDealsChanged.bind(this);
  }

  componentDidMount() {
    const {deal} = this.state;
    const locationId = AccountStore.getLocationId();

    DealRepository.addDealListener(this.onDealsChanged);

    if (deal?.externalServiceId && this.props?.markMessageAsRead) {
      this.props?.markMessageAsRead({
        externalServiceId: String(deal.externalServiceId),
        locationId: locationId,
        messageType: MessageTypeEnum.deal,
      });

      Events.Promotion.trackEvent(
        deal.externalServiceId.toString(),
        deal.name,
        PromotionActions.ViewedDeal,
        true,
      );
    }
  }

  componentWillUnmount() {
    DealRepository.removeDealListener(this.onDealsChanged);
  }

  onDealsChanged() {
    const deal = DealRepository.getDeal(
      this.state.deal.externalServiceId as string,
    );

    if (deal) {
      this.setState({
        deal,
      });
    }
  }

  async applyDeal() {
    DealHelper.claimDeal(
      this.context,
      this.state.deal.externalServiceId as string,
    );
    FirebaseAnalytic.trackEvent('ApplyNow', 'DealDetailScreen: ApplyDeal', {
      ...this.props,
      ...this.state,
    });
  }

  dealAppliedStatus() {
    return (
      this.state.deal.dealStatus ===
      (DealStatus.accepted || DealStatus.redeemed)
    );
  }

  dealNotAppliedStatus() {
    return this.state.deal.dealStatus === DealStatus.pulled;
  }

  renderApplyButton() {
    if (this.dealNotAppliedStatus()) {
      return getDescriber().dealApplyBtn(this.applyDeal);
    } else if (this.dealAppliedStatus()) {
      return getDescriber().dealAppliedBtn();
    }
    return null;
  }

  render() {
    let uri = this.state.deal.image;

    if (typeof uri === 'string') {
      // force HTTPS
      uri = ensureHttps(uri);
    }
    const transactionDate = getTransactionDate(this.state.deal);

    const dealEndDate = Util.formatDate(transactionDate, 'MMM DD, YYYY');
    const dealEndTime = Util.formatDate(transactionDate, 'hh:mm A');
    const isPromoCreditOffer = this.state.deal?.type === MessageTypeEnum.offers;

    const dealName = isPromoCreditOffer
      ? Localized.Labels.offers_promo_credit_text
      : this.state.deal?.name;
    return (
      <BackSubheader
        style={getDescriber() && getDescriber().backHeaderStyleDescriptor()}
        title={Localized.Labels.offers}
      >
        <View style={getDescriber().getDealDetailStyleDescriptor().content}>
          <ScrollView contentContainerStyle={styles.falseBottomPadding}>
            <View style={getDescriber().getDealDetailStyleDescriptor().topView}>
              {this.state.deal.image ? (
                <View style={styles.imageView}>
                  <Image
                    source={{uri: ensureHttps(this.state.deal?.image)}}
                    style={getDescriber().getDealDetailStyleDescriptor().image}
                  />
                </View>
              ) : (
                <View
                  style={getDescriber().getDealDetailStyleDescriptor().iconView}
                >
                  {getDescriber().dealDefaultIconDescriptor()}
                </View>
              )}
              <View
                style={
                  getDescriber().getDealDetailStyleDescriptor().dealTitleRow
                }
              >
                <AVText
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                  accessibilityLabel={this.state.deal.name}
                  style={[getDescriber().getDealDetailStyleDescriptor().name]}
                >
                  {dealName}
                </AVText>
              </View>
            </View>
            {getDescriber().getDealEndDateTime(
              '' + dealEndDate + ' at ' + dealEndTime,
            )}
            {isPromoCreditOffer && (
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm12}
                accessibilityLabel={this.state.deal.message}
                style={
                  getDescriber().getDealDetailStyleDescriptor().promoCreditDesc
                }
              >
                {this.state.deal?.message}
              </AVText>
            )}

            {!isPromoCreditOffer && (
              <>
                <AVText
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm10}
                  style={
                    getDescriber().getDealDetailStyleDescriptor().description
                  }
                  accessibilityLabel={this.state.deal.description}
                >
                  {this.state.deal.description}
                </AVText>
                <AVText
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm9}
                  style={
                    getDescriber().getDealDetailStyleDescriptor().validUntil
                  }
                >
                  {`${getDescriber().dealValidityKey()} ${Util.formatDate(
                    this.state.deal.endDate,
                    'MM/DD/YYYY',
                  )}`}
                </AVText>
                {this.state.deal.numberOfUsesPerCode > 0 && (
                  <AVText
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm12}
                    style={
                      getDescriber().getDealDetailStyleDescriptor().redeemLimit
                    }
                  >
                    {Localized.Labels.formatString(
                      Localized.Labels.offers_redemption_limit,
                      this.state.deal.numberOfUsesPerCode,
                    )}
                  </AVText>
                )}
                {this.dealAppliedStatus() &&
                  getDescriber().dealAppliedContainer()}
              </>
            )}
          </ScrollView>
        </View>
        {!isPromoCreditOffer && this.renderApplyButton()}
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    padding: Styles.Spacing.m3,
    flex: 1,
    backgroundColor: Styles.tabBarBackgroundColor,
  },
  falseBottomPadding: {
    paddingBottom: 60,
  },
  topView: {
    marginTop: Styles.Spacing.m3,
  },
  imageView: {
    marginLeft: Styles.Spacing.m0,
  },
  icon: {
    width: 50,
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  redeemLimit: {
    marginTop: Styles.Spacing.m3,
    fontSize: Styles.Fonts.f8,
    fontWeight: '400',
    fontFamily: Styles.FontFamily.figtreeRegular,
    alignSelf: 'center',
    color: Styles.Colors.PayNew.darkHuesBase04,
  },
  name: {
    color: Styles.black,
    fontSize: Styles.Fonts.sectionHeader,
    fontFamily: Styles.FontFamily.figtreeRegular,
    fontWeight: '700',
    flex: 1,
  },
  fromContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  message: {
    color: Styles.black,
    fontSize: Styles.Fonts.f1,
  },
  qrcode: {
    alignItems: 'center',
    marginBottom: Styles.Spacing.m3,
  },
  topRow: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: Styles.Spacing.m2 + Styles.Spacing.m1,
  },
  trashContainer: {
    marginBottom: Styles.Spacing.m1,
  },
  image: {
    width: width / 2.4,
    height: width / 2.4,
    borderRadius: Styles.Spacing.m2,
  },
  statusText: {
    fontSize: Styles.Fonts.f1,
    marginTop: Styles.Spacing.m2,
    textAlign: 'center',
    marginBottom: Styles.Spacing.m1,
  },
  statusSection: {
    marginBottom: Styles.Spacing.m3,
  },
  statusProgressContainer: {
    height: Styles.Spacing.m2,
    backgroundColor: Styles.lightGray,
    borderRadius: Styles.Spacing.m2,
    flexDirection: 'row',
  },
  successText: {
    color: Styles.positiveColor,
    fontWeight: 'bold',
  },
});

export default DealDetailScreen;
